/**
 * BOGOS URL param helpers for Ambrosia buy-box sections.
 * On first page load, missing params trigger a full reload so BOGOS can read them.
 * Before checkout, params are merged via replaceState only (no reload).
 *
 * Also prunes Secomapp's sticky `sca_fg_codes` cookie so leftover campaign
 * codes from other landings do not keep applying after cart empty / homepage.
 */
(function initAmbrosiaBogoUrlParams() {
  'use strict';

  var RELOAD_MARKER = '_amb_bogo_reload';
  var SCA_FG_COOKIE = 'sca_fg_codes';

  function normalizeBogoParamString(rawValue) {
    if (!rawValue) return '';
    var normalized = String(rawValue).trim();
    if (!normalized) return '';

    if (normalized.indexOf('://') !== -1) {
      try {
        normalized = new URL(normalized).search.replace(/^\?/, '');
      } catch (err) {}
    }

    var queryStartIndex = normalized.indexOf('?');
    if (queryStartIndex > -1) {
      normalized = normalized.slice(queryStartIndex + 1);
    }

    normalized = normalized.replace(/^[?&]+/, '');

    var hashIndex = normalized.indexOf('#');
    if (hashIndex > -1) {
      normalized = normalized.slice(0, hashIndex);
    }

    return normalized.trim();
  }

  function isInternalParam(key) {
    return key === RELOAD_MARKER;
  }

  function configuredParamsMatchUrl(sanitizedParams) {
    var incoming = new URLSearchParams(sanitizedParams);
    var current = new URLSearchParams(window.location.search);
    var matches = true;

    incoming.forEach(function (value, key) {
      if (!key) return;
      if (current.get(key) !== value) {
        matches = false;
      }
    });

    return matches;
  }

  function mergeParamsIntoUrl(baseUrl, sanitizedParams) {
    var mergedUrl = new URL(baseUrl, window.location.origin);
    var incomingParams = new URLSearchParams(sanitizedParams);
    var hasValidParam = false;

    incomingParams.forEach(function (value, key) {
      if (!key) return;
      mergedUrl.searchParams.set(key, value);
      hasValidParam = true;
    });

    return hasValidParam ? mergedUrl : null;
  }

  function stripInternalParamsFromCurrentUrl() {
    var url = new URL(window.location.href);
    if (!url.searchParams.has(RELOAD_MARKER)) return;

    url.searchParams.delete(RELOAD_MARKER);
    var nextRelativeUrl = url.pathname + (url.search ? url.search : '') + url.hash;
    window.history.replaceState({}, '', nextRelativeUrl);
  }

  function copyPublicSearchParams(sourceParams, targetUrl) {
    sourceParams.forEach(function (value, key) {
      if (!key || isInternalParam(key)) return;
      targetUrl.searchParams.set(key, value);
    });
  }

  function readCookie(name) {
    var parts = String(document.cookie || '').split(';');
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].trim();
      if (!part) continue;
      var eq = part.indexOf('=');
      var key = eq === -1 ? part : part.slice(0, eq);
      if (key !== name) continue;
      var raw = eq === -1 ? '' : part.slice(eq + 1);
      try {
        return decodeURIComponent(raw);
      } catch (err) {
        return raw;
      }
    }
    return '';
  }

  function cookieDomainCandidates() {
    var host = window.location.hostname || '';
    var list = ['', host];
    if (host && host.indexOf('.') !== -1) {
      list.push('.' + host);
      var root = host.replace(/^www\./, '');
      if (root && root !== host) {
        list.push(root);
        list.push('.' + root);
      }
    }
    return list;
  }

  function writeScaFgCodes(codes) {
    var unique = [];
    (codes || []).forEach(function (code) {
      var value = String(code || '').trim();
      if (!value) return;
      if (unique.indexOf(value) === -1) unique.push(value);
    });

    var domains = cookieDomainCandidates();
    var i;

    if (!unique.length) {
      for (i = 0; i < domains.length; i++) {
        document.cookie =
          SCA_FG_COOKIE +
          '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;' +
          (domains[i] ? ' domain=' + domains[i] + ';' : '');
      }
      return;
    }

    var encoded = encodeURIComponent(JSON.stringify(unique));
    for (i = 0; i < domains.length; i++) {
      document.cookie =
        SCA_FG_COOKIE +
        '=' +
        encoded +
        '; path=/;' +
        (domains[i] ? ' domain=' + domains[i] + ';' : '');
    }
  }

  function readScaFgCodes() {
    var raw = readCookie(SCA_FG_COOKIE);
    if (!raw) return [];
    try {
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map(function (value) {
          return String(value || '').trim();
        })
        .filter(Boolean);
    } catch (err) {
      return [];
    }
  }

  /**
   * Prefer freegifts_code on the current URL; fall back to the first enabled
   * buy-box section config on this page (landings often inject URL later).
   */
  function currentFreegiftsCode() {
    try {
      var fromUrl = new URLSearchParams(window.location.search).get('freegifts_code');
      if (fromUrl && String(fromUrl).trim()) return String(fromUrl).trim();
    } catch (err) {}

    var section = document.querySelector(
      '[data-bogo-url-params-enabled="true"][data-bogo-url-params]'
    );
    if (!section) return '';
    var sanitized = normalizeBogoParamString(section.getAttribute('data-bogo-url-params') || '');
    if (!sanitized) return '';
    try {
      var fromSection = new URLSearchParams(sanitized).get('freegifts_code');
      return fromSection ? String(fromSection).trim() : '';
    } catch (err2) {
      return '';
    }
  }

  function isHomepage() {
    if (document.body && document.body.classList.contains('template-index')) return true;
    var path = (window.location.pathname || '').replace(/\/+$/, '');
    return path === '' || path === '/';
  }

  /**
   * Keep only the freegifts_code for the current landing/URL (if any).
   * Drops leftover codes from other campaigns in sca_fg_codes.
   * @returns {string[]} codes kept
   */
  function pruneScaFgCodesToCurrentLanding() {
    var keep = currentFreegiftsCode();
    var existing = readScaFgCodes();
    var next = keep ? [keep] : [];

    var same =
      existing.length === next.length &&
      existing.every(function (code, index) {
        return code === next[index];
      });
    if (!same) writeScaFgCodes(next);
    return next;
  }

  /**
   * @param {{ bogoUrlParamsEnabled?: boolean, bogoUrlParams?: string }} sectionConfig
   * @param {{ allowReload?: boolean }} options
   * @returns {boolean} true when a full-page reload was triggered
   */
  function apply(sectionConfig, options) {
    var allowReload = !!(options && options.allowReload);
    if (!sectionConfig || !sectionConfig.bogoUrlParamsEnabled) return false;

    var sanitizedParams = normalizeBogoParamString(sectionConfig.bogoUrlParams);
    if (!sanitizedParams) return false;

    if (configuredParamsMatchUrl(sanitizedParams)) {
      stripInternalParamsFromCurrentUrl();
      return false;
    }

    var mergedUrl = mergeParamsIntoUrl(window.location.href, sanitizedParams);
    if (!mergedUrl) return false;

    if (allowReload) {
      var currentUrl = new URL(window.location.href);
      if (!currentUrl.searchParams.has(RELOAD_MARKER)) {
        mergedUrl.searchParams.set(RELOAD_MARKER, '1');
        window.location.replace(mergedUrl.toString());
        return true;
      }
    }

    var nextRelativeUrl =
      mergedUrl.pathname + (mergedUrl.search ? mergedUrl.search : '') + mergedUrl.hash;
    window.history.replaceState({}, '', nextRelativeUrl);
    return false;
  }

  window.AmbrosiaBogoUrlParams = {
    normalize: normalizeBogoParamString,
    apply: apply,
    copyPublicSearchParams: copyPublicSearchParams,
    isInternalParam: isInternalParam,
    currentFreegiftsCode: currentFreegiftsCode,
    pruneScaFgCodesToCurrentLanding: pruneScaFgCodesToCurrentLanding,
    isHomepage: isHomepage,
  };
})();
